import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, PencilSimple, Trash, X, BookOpen, UploadSimple } from "@phosphor-icons/react";
import { useAuth } from "@/lib/AuthContext";
import LibraryGuide from "@/components/LibraryGuide";

const rupees = (paise) => `₹${(paise / 100).toLocaleString("en-IN")}`;

const Field = ({ label, value, onChange, type = "text", textarea, testid }) => (
  <label className="block mb-3">
    <span className="label-micro">{label}</span>
    {textarea ? (
      <textarea rows={3} value={value ?? ""} onChange={e => onChange(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                data-testid={testid}/>
    ) : (
      <input type={type} value={value ?? ""} onChange={e => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
             className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
             data-testid={testid}/>
    )}
  </label>
);

function Modal({ title, onClose, children, onSave, saving }) {
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-6" data-testid="lib-modal">
      <div className="bg-white border border-slate-200 w-full max-w-xl p-6 rounded-sm max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold">{title}</h3>
          <button onClick={onClose} data-testid="lib-modal-close"><X size={18}/></button>
        </div>
        {children}
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 border border-slate-300 rounded-sm hover:bg-slate-100">Cancel</button>
          <button onClick={onSave} disabled={saving} data-testid="lib-modal-save" className="text-sm px-4 py-2 bg-slate-900 text-white rounded-sm hover:bg-blue-700 transition-colors duration-200 disabled:opacity-60">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

const BulkUploadButton = ({ endpoint, testid, sampleHeader, onDone }) => {
  const [uploading, setUploading] = useState(false);
  const inputId = `bulk-${testid}`;
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a .csv file"); e.target.value = ""; return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("CSV too large (max 2 MB)"); e.target.value = ""; return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const msg = `Imported ${data.inserted}. Skipped ${data.skipped}.`;
      if (data.errors?.length) {
        toast.warning(`${msg} ${data.errors.length} row(s) had errors — first: row ${data.errors[0].row} — ${data.errors[0].reason}`);
      } else {
        toast.success(msg);
      }
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const downloadSample = () => {
    const blob = new Blob([sampleHeader + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${testid}-sample.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };
  return (
    <div className="inline-flex items-center gap-2">
      <button type="button" onClick={downloadSample} data-testid={`${testid}-sample`} className="text-xs text-blue-700 hover:underline">
        Sample CSV
      </button>
      <label htmlFor={inputId} className={`inline-flex items-center gap-2 border border-slate-300 text-slate-900 hover:bg-slate-900 hover:text-white text-sm font-semibold px-4 py-2 rounded-sm cursor-pointer transition-colors duration-200 ${uploading ? "opacity-60 cursor-wait" : ""}`} data-testid={`${testid}-btn`}>
        <UploadSimple size={14}/> {uploading ? "Importing…" : "Bulk upload CSV"}
        <input id={inputId} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid={`${testid}-input`} disabled={uploading}/>
      </label>
    </div>
  );
};

function FeaturesTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/library/features").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const blank = () => ({ name: "", module: "Vision", verticals: ["retail"], client_outcome: "", compute_class: "gpu",
    camera_requirements: "", accuracy_position: "", exclusion_text: "", dependencies: [], requires_approval: false, deployment_phase: 1, status: "live", version: 1 });

  const save = async () => {
    setSaving(true);
    try {
      if (edit.id) await api.put(`/library/features/${edit.id}`, edit);
      else await api.post("/library/features", edit);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete ${f.name}?`)) return;
    try { await api.delete(`/library/features/${f.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <>
      {isAdmin && (
        <div className="mt-4 flex justify-end items-center gap-3">
          <BulkUploadButton
            endpoint="/library/features/bulk"
            testid="feat-bulk"
            sampleHeader="name,module,verticals,client_outcome,compute_class,deployment_phase,camera_requirements,accuracy_position,exclusion_text,requires_approval,dependencies,status"
            onDone={load}
          />
          <button onClick={() => setEdit(blank())} data-testid="feat-new" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-sm transition-colors duration-200"><Plus size={14}/>New feature</button>
        </div>
      )}
      <div className="bg-white border border-slate-200 mt-4 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left">
            <th className="px-4 py-2 label-micro">Name</th><th className="px-4 py-2 label-micro">Module</th>
            <th className="px-4 py-2 label-micro">Compute</th><th className="px-4 py-2 label-micro">Deps</th>
            <th className="px-4 py-2 label-micro">Client Outcome</th>
            {isAdmin && <th className="px-4 py-2 label-micro w-32">Actions</th>}
          </tr></thead>
          <tbody>{items.map(f => (
            <tr key={f.id} className="border-b border-slate-100" data-testid={`feat-row-${f.name}`}>
              <td className="px-4 py-3 font-semibold">{f.name}</td>
              <td className="px-4 py-3 text-slate-600">{f.module}</td>
              <td className="px-4 py-3"><span className="label-micro">{f.compute_class}</span></td>
              <td className="px-4 py-3 tabular-nums">{(f.dependencies||[]).length}</td>
              <td className="px-4 py-3 text-slate-700 max-w-md">{f.client_outcome}</td>
              {isAdmin && (
                <td className="px-4 py-3">
                  <button onClick={() => setEdit({ ...f })} data-testid={`feat-edit-${f.name}`} className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2 py-1 hover:bg-slate-100 mr-2"><PencilSimple size={12}/></button>
                  <button onClick={() => del(f)} data-testid={`feat-delete-${f.name}`} className="text-xs inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-2 py-1 hover:bg-red-50"><Trash size={12}/></button>
                </td>
              )}
            </tr>
          ))}</tbody>
        </table>
      </div>
      {edit && (
        <Modal title={edit.id ? "Edit feature" : "New feature"} onClose={() => setEdit(null)} onSave={save} saving={saving}>
          <Field label="Name" value={edit.name} onChange={v => setEdit({...edit, name: v})} testid="feat-f-name"/>
          <Field label="Module" value={edit.module} onChange={v => setEdit({...edit, module: v})} testid="feat-f-module"/>
          <Field label="Client outcome" textarea value={edit.client_outcome} onChange={v => setEdit({...edit, client_outcome: v})} testid="feat-f-outcome"/>
          <Field label="Camera requirements" textarea value={edit.camera_requirements} onChange={v => setEdit({...edit, camera_requirements: v})} testid="feat-f-cam"/>
          <Field label="Accuracy position" textarea value={edit.accuracy_position} onChange={v => setEdit({...edit, accuracy_position: v})}/>
          <Field label="Exclusion text" textarea value={edit.exclusion_text} onChange={v => setEdit({...edit, exclusion_text: v})}/>
          <div className="grid grid-cols-2 gap-3">
            <label><span className="label-micro">Compute class</span>
              <select value={edit.compute_class} onChange={e => setEdit({...edit, compute_class: e.target.value})} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"><option>cpu</option><option>gpu</option></select></label>
            <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!edit.requires_approval} onChange={e => setEdit({...edit, requires_approval: e.target.checked})}/><span className="text-sm">Requires approval</span></label>
          </div>
        </Modal>
      )}
    </>
  );
}

function ClausesTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/library/clauses").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const blank = () => ({ key: "", title: "", section_binding: "commercial_terms", body: "", applicability_condition: "always", requires_approval: false, version: 1 });
  const save = async () => {
    setSaving(true);
    try {
      if (edit.id) await api.put(`/library/clauses/${edit.id}`, edit); else await api.post("/library/clauses", edit);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error("Failed"); } finally { setSaving(false); }
  };
  const del = async (c) => { if (!window.confirm(`Delete ${c.title}?`)) return; await api.delete(`/library/clauses/${c.id}`); load(); };

  return (
    <>
      {isAdmin && <div className="mt-4 flex justify-end"><button onClick={() => setEdit(blank())} data-testid="clause-new" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-sm transition-colors duration-200"><Plus size={14}/>New clause</button></div>}
      <div className="mt-4 space-y-3">
        {items.map(c => (
          <div key={c.id} className="bg-white border border-slate-200 p-5" data-testid={`clause-row-${c.key}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display font-bold">{c.title}</div>
                <span className="label-micro">{c.key} · v{c.version} · {c.section_binding}</span>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <button onClick={() => setEdit({ ...c })} data-testid={`clause-edit-${c.key}`} className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2 py-1 hover:bg-slate-100"><PencilSimple size={12}/></button>
                  <button onClick={() => del(c)} data-testid={`clause-delete-${c.key}`} className="text-xs inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-2 py-1 hover:bg-red-50"><Trash size={12}/></button>
                </div>
              )}
            </div>
            <p className="text-sm text-slate-700 mt-2 leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>
      {edit && (
        <Modal title={edit.id ? "Edit clause" : "New clause"} onClose={() => setEdit(null)} onSave={save} saving={saving}>
          <Field label="Key (unique)" value={edit.key} onChange={v => setEdit({...edit, key: v})} testid="clause-f-key"/>
          <Field label="Title" value={edit.title} onChange={v => setEdit({...edit, title: v})} testid="clause-f-title"/>
          <Field label="Section binding" value={edit.section_binding} onChange={v => setEdit({...edit, section_binding: v})}/>
          <Field label="Body" textarea value={edit.body} onChange={v => setEdit({...edit, body: v})} testid="clause-f-body"/>
          <Field label="Applicability" value={edit.applicability_condition} onChange={v => setEdit({...edit, applicability_condition: v})}/>
        </Modal>
      )}
    </>
  );
}

function PriceBookTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/library/price_book").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const blank = () => ({ charge_code: "", label: "", charge_type: "one_time", basis: "per_project", list_price: 0, floor_price: 0, calculation_type: "per_unit_flat", tiers: [], applicable_verticals: ["retail"], inclusions_text: "", version: 1 });
  const save = async () => {
    setSaving(true);
    try {
      if (edit.id) await api.put(`/library/price_book/${edit.id}`, edit); else await api.post("/library/price_book", edit);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error("Failed"); } finally { setSaving(false); }
  };
  const del = async (p) => { if (!window.confirm(`Delete ${p.charge_code}?`)) return; await api.delete(`/library/price_book/${p.id}`); load(); };

  return (
    <>
      {isAdmin && <div className="mt-4 flex justify-end"><button onClick={() => setEdit(blank())} data-testid="price-new" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-sm transition-colors duration-200"><Plus size={14}/>New entry</button></div>}
      <div className="bg-white border border-slate-200 mt-4 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left">
            <th className="px-4 py-2 label-micro">Code</th><th className="px-4 py-2 label-micro">Label</th>
            <th className="px-4 py-2 label-micro">Type</th><th className="px-4 py-2 label-micro">Basis</th>
            <th className="px-4 py-2 label-micro text-right">List</th><th className="px-4 py-2 label-micro text-right">Floor</th>
            {isAdmin && <th className="px-4 py-2 label-micro w-24"></th>}
          </tr></thead>
          <tbody>{items.map(p => (
            <tr key={p.id} className="border-b border-slate-100" data-testid={`price-row-${p.charge_code}`}>
              <td className="px-4 py-3 font-mono text-xs">{p.charge_code}</td>
              <td className="px-4 py-3 font-semibold">{p.label}</td>
              <td className="px-4 py-3">{p.charge_type}</td>
              <td className="px-4 py-3">{p.basis}</td>
              <td className="px-4 py-3 text-right tabular-nums">{rupees(p.list_price)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-slate-500">{rupees(p.floor_price)}</td>
              {isAdmin && (
                <td className="px-4 py-3">
                  <button onClick={() => setEdit({ ...p })} data-testid={`price-edit-${p.charge_code}`} className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2 py-1 hover:bg-slate-100 mr-1"><PencilSimple size={12}/></button>
                  <button onClick={() => del(p)} data-testid={`price-delete-${p.charge_code}`} className="text-xs inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-2 py-1 hover:bg-red-50"><Trash size={12}/></button>
                </td>
              )}
            </tr>
          ))}</tbody>
        </table>
      </div>
      {edit && (
        <Modal title={edit.id ? "Edit price entry" : "New price entry"} onClose={() => setEdit(null)} onSave={save} saving={saving}>
          <Field label="Charge code (unique)" value={edit.charge_code} onChange={v => setEdit({...edit, charge_code: v})} testid="price-f-code"/>
          <Field label="Label" value={edit.label} onChange={v => setEdit({...edit, label: v})} testid="price-f-label"/>
          <div className="grid grid-cols-2 gap-3">
            <label><span className="label-micro">Charge type</span>
              <select value={edit.charge_type} onChange={e => setEdit({...edit, charge_type: e.target.value})} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm">
                <option value="one_time">one_time</option><option value="recurring">recurring</option><option value="usage">usage</option></select></label>
            <label><span className="label-micro">Basis</span>
              <select value={edit.basis} onChange={e => setEdit({...edit, basis: e.target.value})} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm">
                <option value="per_site">per_site</option><option value="per_camera">per_camera</option><option value="per_node">per_node</option>
                <option value="per_feature">per_feature</option><option value="per_project">per_project</option><option value="flat">flat</option></select></label>
          </div>
          <Field label="List price (paise · e.g. 25000000 = ₹250,000)" type="number" value={edit.list_price} onChange={v => setEdit({...edit, list_price: v})} testid="price-f-list"/>
          <Field label="Floor price (paise)" type="number" value={edit.floor_price} onChange={v => setEdit({...edit, floor_price: v})} testid="price-f-floor"/>
          <Field label="Inclusions text" textarea value={edit.inclusions_text} onChange={v => setEdit({...edit, inclusions_text: v})}/>
        </Modal>
      )}
    </>
  );
}

function SuitesTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [features, setFeatures] = useState([]);
  const [priceBook, setPriceBook] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => Promise.all([
    api.get("/library/suite_templates"),
    api.get("/library/features"),
    api.get("/library/price_book"),
  ]).then(([s, f, p]) => { setItems(s.data); setFeatures(f.data); setPriceBook(p.data); });
  useEffect(() => { load(); }, []);
  const blank = () => ({ name: "", vertical: "retail", feature_ids: [], default_charge_codes: [], default_assumptions: [], default_exclusions_ack: [], description: "", version: 1 });
  const toggleFeat = (fid) => setEdit({ ...edit, feature_ids: edit.feature_ids.includes(fid) ? edit.feature_ids.filter(x => x !== fid) : [...edit.feature_ids, fid] });
  const toggleCode = (code) => setEdit({ ...edit, default_charge_codes: (edit.default_charge_codes || []).includes(code) ? edit.default_charge_codes.filter(x => x !== code) : [...(edit.default_charge_codes || []), code] });
  const save = async () => {
    setSaving(true);
    try {
      if (edit.id) await api.put(`/library/suite_templates/${edit.id}`, edit); else await api.post("/library/suite_templates", edit);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error("Failed"); } finally { setSaving(false); }
  };
  const del = async (s) => { if (!window.confirm(`Delete ${s.name}?`)) return; await api.delete(`/library/suite_templates/${s.id}`); load(); };
  const rupees = (paise) => `₹${(paise/100).toLocaleString("en-IN")}`;

  return (
    <>
      {isAdmin && <div className="mt-4 flex justify-end"><button onClick={() => setEdit(blank())} data-testid="suite-new" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-sm transition-colors duration-200"><Plus size={14}/>New suite</button></div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {items.map(s => (
          <div key={s.id} className="bg-white border border-slate-200 p-5" data-testid={`suite-row-${s.name}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold">{s.name}</div>
                <div className="label-micro mt-1">{s.vertical} · {s.feature_ids.length} features · {(s.default_charge_codes||[]).length} auto-pricing lines · {(s.default_assumptions||[]).length} assumptions</div>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <button onClick={() => setEdit({ ...s, default_charge_codes: s.default_charge_codes||[], default_assumptions: s.default_assumptions||[], default_exclusions_ack: s.default_exclusions_ack||[] })} data-testid={`suite-edit-${s.name}`} className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2 py-1 hover:bg-slate-100"><PencilSimple size={12}/></button>
                  <button onClick={() => del(s)} data-testid={`suite-delete-${s.name}`} className="text-xs inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-2 py-1 hover:bg-red-50"><Trash size={12}/></button>
                </div>
              )}
            </div>
            <p className="text-sm text-slate-700 mt-3">{s.description}</p>
          </div>
        ))}
      </div>
      {edit && (
        <Modal title={edit.id ? "Edit suite" : "New suite"} onClose={() => setEdit(null)} onSave={save} saving={saving}>
          <Field label="Name" value={edit.name} onChange={v => setEdit({...edit, name: v})} testid="suite-f-name"/>
          <Field label="Vertical" value={edit.vertical} onChange={v => setEdit({...edit, vertical: v})}/>
          <Field label="Description" textarea value={edit.description} onChange={v => setEdit({...edit, description: v})}/>
          <div className="mb-3">
            <span className="label-micro">Features included ({edit.feature_ids.length})</span>
            <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-2">
              {features.map(f => (
                <label key={f.id} className="flex items-center gap-2 py-1 text-sm">
                  <input type="checkbox" checked={edit.feature_ids.includes(f.id)} onChange={() => toggleFeat(f.id)}/>
                  {f.name} <span className="label-micro">{f.module}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="mb-3">
            <span className="label-micro">Default pricing lines — auto-load when this suite is applied ({(edit.default_charge_codes||[]).length})</span>
            <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 rounded-sm p-2" data-testid="suite-default-codes">
              {priceBook.map(p => (
                <label key={p.id} className="flex items-center justify-between gap-2 py-1 text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <input type="checkbox" checked={(edit.default_charge_codes||[]).includes(p.charge_code)} onChange={() => toggleCode(p.charge_code)} data-testid={`suite-code-${p.charge_code}`}/>
                    <span className="truncate">{p.label}</span>
                    <span className="label-micro shrink-0">{p.charge_code} · {p.charge_type} · {p.basis.replace(/_/g,' ')}</span>
                  </span>
                  <span className="text-xs tabular-nums font-semibold shrink-0">{rupees(p.list_price)}</span>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Per-camera lines auto-populate qty from total cameras; per-site lines from total sites.</p>
          </div>
          <div className="mb-3">
            <label className="block"><span className="label-micro">Default assumptions (one per line) — preloaded into the proposal's Outcomes step</span>
              <textarea rows={4} value={(edit.default_assumptions||[]).join("\n")} onChange={(e) => setEdit({...edit, default_assumptions: e.target.value.split("\n").map(s=>s.trim()).filter(Boolean)})}
                data-testid="suite-f-assumptions"
                className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Client provides working CCTV cameras&#10;Client grants POS access on day 1"/></label>
          </div>
          <div className="mb-3">
            <label className="block"><span className="label-micro">Default exclusions to acknowledge (one per line)</span>
              <textarea rows={3} value={(edit.default_exclusions_ack||[]).join("\n")} onChange={(e) => setEdit({...edit, default_exclusions_ack: e.target.value.split("\n").map(s=>s.trim()).filter(Boolean)})}
                data-testid="suite-f-exclusions"
                className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
          </div>
        </Modal>
      )}
    </>
  );
}

function PrereqTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/library/prerequisites").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const blank = () => ({ key: "", text: "", category: "client", version: 1 });
  const save = async () => {
    setSaving(true);
    try {
      if (edit.id) await api.put(`/library/prerequisites/${edit.id}`, edit); else await api.post("/library/prerequisites", edit);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error("Failed"); } finally { setSaving(false); }
  };
  const del = async (p) => { if (!window.confirm(`Delete ${p.key}?`)) return; await api.delete(`/library/prerequisites/${p.id}`); load(); };
  return (
    <>
      {isAdmin && (
        <div className="mt-4 flex justify-end items-center gap-3">
          <BulkUploadButton
            endpoint="/library/prerequisites/bulk"
            testid="prereq-bulk"
            sampleHeader="key,text,category"
            onDone={load}
          />
          <button onClick={() => setEdit(blank())} data-testid="prereq-new" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-sm transition-colors duration-200"><Plus size={14}/>New prerequisite</button>
        </div>
      )}
      <div className="bg-white border border-slate-200 mt-4">
        {items.map(p => (
          <div key={p.id} className="border-b border-slate-100 last:border-b-0 px-5 py-4 flex items-start justify-between" data-testid={`prereq-row-${p.key}`}>
            <div className="flex-1">
              <div className="flex items-center justify-between"><div className="font-semibold text-slate-900">{p.key}</div><span className="label-micro">{p.category}</span></div>
              <p className="text-sm text-slate-700 mt-1">{p.text}</p>
            </div>
            {isAdmin && (
              <div className="flex gap-2 ml-4">
                <button onClick={() => setEdit({ ...p })} className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2 py-1 hover:bg-slate-100"><PencilSimple size={12}/></button>
                <button onClick={() => del(p)} className="text-xs inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-2 py-1 hover:bg-red-50"><Trash size={12}/></button>
              </div>
            )}
          </div>
        ))}
      </div>
      {edit && (
        <Modal title={edit.id ? "Edit prerequisite" : "New prerequisite"} onClose={() => setEdit(null)} onSave={save} saving={saving}>
          <Field label="Key" value={edit.key} onChange={v => setEdit({...edit, key: v})} testid="prereq-f-key"/>
          <Field label="Text" textarea value={edit.text} onChange={v => setEdit({...edit, text: v})} testid="prereq-f-text"/>
          <label><span className="label-micro">Category</span>
            <select value={edit.category} onChange={e => setEdit({...edit, category: e.target.value})} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"><option value="client">client</option><option value="vendor">vendor</option></select></label>
        </Modal>
      )}
    </>
  );
}

export default function LibrariesPage() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");
  const [guideOpen, setGuideOpen] = useState(() => {
    try { return !localStorage.getItem("sakshi_lib_tour_seen"); } catch { return false; }
  });
  const openGuide = () => setGuideOpen(true);
  const closeGuide = () => { setGuideOpen(false); try { localStorage.setItem("sakshi_lib_tour_seen", "1"); } catch (err) { console.warn("could not persist tour flag", err); } };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="libraries-page">
      <div className="flex items-start justify-between gap-3 sm:gap-6 flex-wrap">
        <div>
          <div className="label-micro">Content</div>
          <h1 className="font-display text-3xl font-black mt-1">Libraries</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">Versioned, library-first content. {isAdmin ? "You have edit access." : "Read-only view — contact an admin to edit."}</p>
        </div>
        <button onClick={openGuide} data-testid="lib-show-guide" className="shrink-0 inline-flex items-center gap-2 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white text-sm font-semibold px-4 py-2 rounded-sm transition-colors duration-200">
          <BookOpen size={14} weight="fill"/> Show guide
        </button>
      </div>

      <Tabs defaultValue="features" className="mt-6">
        <TabsList data-testid="library-tabs">
          <TabsTrigger value="features" data-testid="tab-features">Features</TabsTrigger>
          <TabsTrigger value="clauses" data-testid="tab-clauses">Clauses</TabsTrigger>
          <TabsTrigger value="price_book" data-testid="tab-price-book">Price Book</TabsTrigger>
          <TabsTrigger value="suites" data-testid="tab-suites">Suite Templates</TabsTrigger>
          <TabsTrigger value="prereqs" data-testid="tab-prereqs">Prerequisites</TabsTrigger>
        </TabsList>
        <TabsContent value="features"><FeaturesTab isAdmin={isAdmin}/></TabsContent>
        <TabsContent value="clauses"><ClausesTab isAdmin={isAdmin}/></TabsContent>
        <TabsContent value="price_book"><PriceBookTab isAdmin={isAdmin}/></TabsContent>
        <TabsContent value="suites"><SuitesTab isAdmin={isAdmin}/></TabsContent>
        <TabsContent value="prereqs"><PrereqTab isAdmin={isAdmin}/></TabsContent>
      </Tabs>

      <LibraryGuide open={guideOpen} onClose={closeGuide} />
    </div>
  );
}
